import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { aboutData } from '../mock';
import { projectsAPI } from '../utils/api';
import { toast } from '../hooks/use-toast';

const Home = () => {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedProjects = async () => {
      try {
        const response = await projectsAPI.getAll();
        setFeaturedProjects(response.data.projects.slice(0, 6));
      } catch (error) {
        // Error handling - consider using error logging service in production
        toast({
          title: "Error",
          description: "Failed to load featured projects.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only fetch once on component mount

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-gray-900 mb-8 leading-[0.95] tracking-tight">
              25 years of designing for people and place
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed max-w-3xl">
              Our practice connects communities and is committed to the stewardship of people, place, and the environment.
            </p>
            <Link to="/about">
              <button className="text-gray-900 hover:text-gray-600 transition-colors text-lg font-normal inline-flex items-center gap-2 group">
                Learn about us
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Projects Grid */}
      <section className="pb-24 px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">Loading projects...</p>
            </div>
          ) : (
            <div className="space-y-8">
              {featuredProjects.map((project, index) => (
                <Link key={project.id} to={`/projects/${project.id}`}>
                  <div className="group cursor-pointer">
                    <div className="relative h-[70vh] overflow-hidden mb-6">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="max-w-2xl">
                      <p className="text-sm uppercase tracking-wider text-gray-500 mb-3">
                        {project.category}
                      </p>
                      <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 group-hover:text-gray-600 transition-colors">
                        {project.title}
                      </h2>
                      <p className="text-xl text-gray-600 mb-2">{project.client}</p>
                      <p className="text-base text-gray-500">{project.location}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Discover what we're working on today
            </h2>
            <Link to="/projects">
              <button className="text-gray-900 hover:text-gray-600 transition-colors text-lg font-normal inline-flex items-center gap-2 group">
                See all work
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;